/**
 * useEstimatedTime - Calculate estimated time for a ticket in queue
 *
 * Calculates when a ticket will be served based on:
 * - Current position in queue
 * - Estimated wait time of all previous tickets
 * - Updates every 30 seconds
 */

import { useState, useEffect } from 'react';
import type { QueueTicket } from '../types';

export interface EstimatedTime {
  minutes: number;
  time: string; // HH:MM format
  isNow: boolean;
}

interface UseEstimatedTimeProps {
  tickets: QueueTicket[];
  currentPosition: number;
}

export function useEstimatedTime({ tickets, currentPosition }: UseEstimatedTimeProps): EstimatedTime {
  const [estimatedTime, setEstimatedTime] = useState<EstimatedTime>({
    minutes: 0,
    time: '--:--',
    isNow: currentPosition === 1,
  });

  useEffect(() => {
    const calculateTime = () => {
      // First ticket is "Now"
      if (currentPosition === 1 || tickets.length === 0) {
        setEstimatedTime({
          minutes: 0,
          time: 'Ahora',
          isNow: true,
        });
        return;
      }

      // Calculate accumulated wait time from all previous tickets
      let accumulatedMinutes = 0;

      for (let i = 0; i < tickets.length; i++) {
        const ticket = tickets[i];

        // Only count tickets before current position
        if (ticket.position < currentPosition) {
          // Use estimatedWaitTime from ticket, or default to 30 minutes
          const ticketWaitTime = ticket.estimatedWaitTime || 30;
          accumulatedMinutes += ticketWaitTime;
        }
      }

      // Calculate estimated time
      const now = new Date();
      const estimatedDate = new Date(now.getTime() + accumulatedMinutes * 60000);

      const hours = estimatedDate.getHours().toString().padStart(2, '0');
      const minutes = estimatedDate.getMinutes().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}`;

      setEstimatedTime({
        minutes: accumulatedMinutes,
        time: timeString,
        isNow: false,
      });
    };

    // Calculate immediately
    calculateTime();

    // Update every 30 seconds
    const interval = setInterval(calculateTime, 30000);

    return () => clearInterval(interval);
  }, [tickets, currentPosition]);

  return estimatedTime;
}
